import { useEffect, useMemo, useState } from "react";
import {
  cldImage,
  cldBlur,
  cldSrcSet,
  isCloudinaryEnabled,
} from "@/lib/cloudinary";

/**
 * <CloudinaryImage /> — drop-in replacement for <img>.
 *
 *  publicId  Public ID inside root folder ("rooms/couple").
 *  fallback  Local/external URL to use when Cloudinary is not configured or
 *            when the Cloudinary asset does not exist yet.
 *  eager     Loads eagerly + fetchpriority=high (for hero images).
 *
 * Strategy
 *   1. Render the fallback URL FIRST (guaranteed to load — no broken icons).
 *   2. In the background, pre-check the Cloudinary URL with an Image().
 *   3. Only when the Cloudinary asset loads successfully, upgrade the visible
 *      src (and srcset) to the Cloudinary URL.
 *
 * This means as soon as you upload an asset to Cloudinary with the matching
 * Public ID, the site will begin serving the optimised Cloudinary version on
 * the next visit — with zero code changes and zero broken states in between.
 */
export default function CloudinaryImage({
  publicId,
  fallback,
  alt = "",
  className = "",
  eager = false,
  sizes = "(min-width: 1200px) 1200px, 100vw",
  width,
  height,
  ...rest
}) {
  const useCld = isCloudinaryEnabled() && Boolean(publicId);
  const cldSrc = useMemo(
    () => (useCld ? cldImage(publicId, { width, height }) : null),
    [useCld, publicId, width, height]
  );
  const cldSet = useMemo(() => (useCld ? cldSrcSet(publicId) : null), [
    useCld,
    publicId,
  ]);
  const blur = useMemo(() => (useCld ? cldBlur(publicId) : null), [
    useCld,
    publicId,
  ]);

  const [upgraded, setUpgraded] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!useCld || !cldSrc) return;
    let cancelled = false;
    const tester = new Image();
    tester.onload = () => {
      if (!cancelled) setUpgraded(true);
    };
    tester.onerror = () => {
      // Cloudinary asset not present yet — keep using fallback.
      if (!cancelled) setUpgraded(false);
    };
    tester.src = cldSrc;
    return () => {
      cancelled = true;
      tester.onload = null;
      tester.onerror = null;
    };
  }, [useCld, cldSrc]);

  const currentSrc = upgraded ? cldSrc : fallback;
  const currentSet = upgraded ? cldSet : undefined;

  return (
    <>
      {upgraded && !loaded && blur && (
        <img
          src={blur}
          alt=""
          aria-hidden="true"
          className={`${className} !absolute inset-0 scale-105 blur-2xl`}
          style={{ objectFit: "cover" }}
        />
      )}
      <img
        src={currentSrc}
        srcSet={currentSet || undefined}
        sizes={currentSet ? sizes : undefined}
        alt={alt}
        width={width}
        height={height}
        loading={eager ? "eager" : "lazy"}
        decoding="async"
        {...(eager ? { fetchPriority: "high" } : {})}
        onLoad={() => setLoaded(true)}
        className={className}
        {...rest}
      />
    </>
  );
}
